import type { OfflineDb } from '../infrastructure/dexie/offline-db.js';

/**
 * ORPHANED_BLOB_MAX_AGE_HOURS (coordinator finding #5) — a captured photo
 * whose outbox row was NEVER created (the operator abandoned the form
 * before submitting: navigated away, closed the tab, killed the app) has no
 * natural cleanup path — `cleanupSyncedBlobs` (Slice 4) deliberately leaves
 * a blob alone when its outbox row is missing entirely. Named constant so
 * the grace window is easy to find/tune, same pattern as
 * `PHOTO_TOKEN_PURGE_GRACE_MINUTES` (Slice 4, backend).
 */
export const ORPHANED_BLOB_MAX_AGE_HOURS = 24;

/**
 * cleanupOrphanedBlobs (coordinator finding #5; second adversarial review
 * BLOCKER #1 corrected the membership check) — sweeps the `blobs` table for
 * rows NOT referenced by any live outbox item's `photoRefs`, older than
 * `maxAgeHours`. Deliberately keys off `photoRefs` MEMBERSHIP rather than a
 * matching `outboxId` FIELD: a blob whose `outboxId` still equals a live
 * item's id but was superseded (a retake replaced it, or it's a stale link
 * left over from a pre-enqueue id change) is genuinely unreferenced and must
 * still be swept — otherwise it survives forever as dead weight. A blob
 * actually listed in ANY item's `photoRefs` (its state or age irrelevant) is
 * never touched — that is `cleanupSyncedBlobs`'s own job, and an unsynced
 * item's blobs must survive indefinitely (design D12). A blob with no
 * `createdAt` at all (should not happen post-Slice-6, but defensive) is
 * NEVER treated as eligible — this sweep only ever deletes what it can PROVE
 * is old, never what it merely assumes.
 */
export async function cleanupOrphanedBlobs(
  db: OfflineDb,
  now: Date = new Date(),
  maxAgeHours: number = ORPHANED_BLOB_MAX_AGE_HOURS,
): Promise<number> {
  const threshold = new Date(now.getTime() - maxAgeHours * 60 * 60 * 1000).toISOString();
  const allBlobs = await db.blobs.toArray();
  // Third adversarial review finding H3 — incident photos are now local
  // blobs too (`incidentPhotoRefs`), covered by this SAME membership sweep.
  const referencedBlobIds = new Set(
    (await db.outbox.toArray()).flatMap((row) => [
      ...((row.photoRefs as string[] | undefined) ?? []),
      ...((row.incidentPhotoRefs as string[] | undefined) ?? []),
    ]),
  );

  let removed = 0;
  for (const blob of allBlobs) {
    if (referencedBlobIds.has(blob.id)) continue;
    if (typeof blob.createdAt !== 'string' || blob.createdAt >= threshold) continue;

    await db.blobs.delete(blob.id);
    removed++;
  }

  return removed;
}
